/**
 * currency conversion utilities for electricity prices
 * converts EUR-based prices to other currencies using exchange rates
 */

/**
 * convert a EUR price to a target currency
 * @param {number} eurAmount - amount in EUR
 * @param {string} targetCurrency - target currency code (e.g., 'NOK', 'SEK')
 * @param {Object} exchangeRates - exchange rates object with currency codes as keys
 * @returns {number} - converted amount in target currency
 */
export function convertCurrency(eurAmount, targetCurrency, exchangeRates) {
  if (!eurAmount || eurAmount === 0) return 0;

  if (targetCurrency === 'EUR') {
    return eurAmount;
  }

  if (!exchangeRates || !exchangeRates[targetCurrency]) {
    console.warn(
      `Exchange rate not found for ${targetCurrency}, returning EUR value`
    );
    return eurAmount;
  }

  const rate = exchangeRates[targetCurrency];
  return eurAmount * rate;
}

/**
 * get currency symbol for display
 * @param {string} currencyCode - currency code (e.g., 'NOK', 'EUR')
 * @returns {string} - currency symbol
 */
export function getCurrencySymbol(currencyCode) {
  const symbols = {
    EUR: '€',
    NOK: 'kr',
    SEK: 'kr',
    DKK: 'kr',
    CHF: 'CHF',
    GBP: '£',
    PLN: 'zł',
    CZK: 'Kč',
    HUF: 'Ft',
    RON: 'lei',
    BGN: 'лв',
    TRY: '₺',
    USD: '$',
  };

  return symbols[currencyCode] || currencyCode;
}

/**
 * format price with currency
 * @param {number} amount - price amount
 * @param {string} currency - currency code
 * @param {number} decimals - number of decimal places (default: 2)
 * @returns {string} - formatted price string
 */
export function formatPrice(amount, currency, decimals = 2) {
  if (amount === null || amount === undefined) return 'N/A';

  const formatted = amount.toFixed(decimals);
  const symbol = getCurrencySymbol(currency);

  // for NOK, SEK, DKK, put symbol after number
  if (['NOK', 'SEK', 'DKK'].includes(currency)) {
    return `${formatted} ${symbol}`;
  }

  // for text-based currency codes (CHF, etc.), add space after symbol
  if (['CHF', 'HUF', 'RON'].includes(currency)) {
    return `${symbol} ${formatted}`;
  }

  // for most other currencies, put symbol before
  return `${symbol}${formatted}`;
}

/**
 * convert EUR price to target currency and format with currency symbol
 * @param {number} eurAmount - price in EUR
 * @param {string} targetCurrency - target currency code
 * @param {Object} exchangeRates - exchange rates object
 * @param {number} decimals - number of decimal places (optional, will use getDecimalPlaces if not provided)
 * @returns {string} - formatted price string with currency symbol
 */
export function formatPriceWithConversion(
  eurAmount,
  targetCurrency,
  exchangeRates,
  decimals = null
) {
  if (eurAmount === null || eurAmount === undefined) return 'N/A';

  const convertedAmount = convertCurrency(
    eurAmount,
    targetCurrency,
    exchangeRates
  );
  const decimalPlaces =
    decimals !== null ? decimals : getDecimalPlaces(targetCurrency);

  return formatPrice(convertedAmount, targetCurrency, decimalPlaces);
}

/**
 * get unit label with currency for electricity prices
 * @param {string} currency - currency code
 * @returns {string} - unit label (e.g., 'EUR/kWh', 'NOK/kWh')
 */
export function getPriceUnit(currency) {
  return `${currency}/kWh`;
}

/**
 * @param {string} currency - Currency code
 * @returns {number} - Number of decimal places
 */
export function getDecimalPlaces(currency) {
  return ['EUR', 'USD'].includes(currency) ? 3 : 2;
}
